import { useMemo } from 'react';
import { useGroup } from '../../queries/use-group';
import { useWorkpapers } from '../../queries/use-workpapers';
import { WorkpaperStatusEnum, Workpaper } from '../../types/workpaper';
import { isMock } from '../../utilities/is-mock';
import { Menu } from '@headlessui/react';
import WorkpaperQuickNavigateEntityName from './WorkpaperQuickNavigateEntityName';
import WorkpaperQuickNavigateWorkpaperDetails from './WorkpaperQuickNavigateWorkpaperDetails';

type WorkpaperQuickNavigateDropDownProps = {
    collapsed: boolean;
    entityId?: string;
    workpaper: Workpaper;
};

function WorkpaperQuickNavigateDropdown({ collapsed, entityId, workpaper }: WorkpaperQuickNavigateDropDownProps): JSX.Element {
    const groupQuery = useGroup(workpaper?.groups && workpaper.groups.length === 1 ? workpaper.groups[0].id : undefined);
    const group = groupQuery.data?.group;

    const workpapersQuery = useWorkpapers({
        page: 1,
        limit: 100,
        filter: isMock()
            ? encodeURIComponent(
                  JSON.stringify({ entity: group?.entities.map((ent) => ent.id) || entityId, status: WorkpaperStatusEnum.ACTIVE })
              )
            : `status=${WorkpaperStatusEnum.ACTIVE}&${(group?.entities.map((ent) => ent.id) || [entityId])
                  .map((id) => `entity=${id}`)
                  .join('&')}`,
        search: '',
        sort: '',
    });

    const allWorkpapers = useMemo(() => {
        return workpapersQuery.data?.workpapers || [];
    }, [workpapersQuery.data?.workpapers]);

    const availableWorkpapers = useMemo(() => {
        return workpapersQuery.data?.workpapers.filter((j) => j.id !== workpaper.id) || [];
    }, [workpapersQuery.data?.workpapers, workpaper.id]);

    const arrangedWorkpapers = useMemo(() => {
        const arrangedWorkpapers: { [key: string]: Workpaper[] } = {};
        const uniqueEntity = allWorkpapers?.map((j) => j.entity.name).filter((x, i, a) => a.indexOf(x) === i) || [];
        for (const entity of uniqueEntity) {
            arrangedWorkpapers[entity] = allWorkpapers?.filter((j) => j.entity.name === entity);
        }
        if (workpaper.status !== WorkpaperStatusEnum.ACTIVE) {
            if (uniqueEntity.includes(workpaper.entity.name)) {
                arrangedWorkpapers[workpaper.entity.name].push(workpaper);
            } else {
                arrangedWorkpapers[workpaper.entity.name] = [workpaper];
            }
        }
        return arrangedWorkpapers;
    }, [allWorkpapers, workpaper]);

    return (
        <>
            {Object.keys(arrangedWorkpapers)
                .sort((a) => (a === workpaper.entity.name ? -1 : 1))
                .map((key) => {
                    return (
                        <div key={key} className="[&:not(:last-child)]:border-b [&:not(:last-child)]:pb-2 [&:not(:first-child)]:pt-2">
                            <WorkpaperQuickNavigateEntityName entityName={key} />
                            <div className="pl-2">
                                {arrangedWorkpapers[key]
                                    ?.sort((a) => (a.id === workpaper.id ? -1 : 1))
                                    .map((j, idx) => {
                                        return (
                                            <WorkpaperQuickNavigateWorkpaperDetails
                                                key={idx}
                                                workpaper={j}
                                                current={j.id === workpaper.id}
                                            />
                                        );
                                    })}
                            </div>
                        </div>
                    );
                })}
            {availableWorkpapers?.length === 0 && (
                <p className="text-gray-500 dark:text-gray-400 text-center pt-4 pb-2 cursor-default">No other workpapers.</p>
            )}
        </>
    );
}

export default WorkpaperQuickNavigateDropdown;
